import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  authenticateAccount,
  saveAccountSession,
  Account,
} from "../config/accounts";

interface LoginFormProps {
  onLoginSuccess: (account: Account) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      if (!username || !password) {
        setError("Vui lòng nhập tên đăng nhập và mật khẩu");
        setIsLoading(false);
        return;
      }

      const account = authenticateAccount(username, password);

      if (!account) {
        setError("Tên đăng nhập hoặc mật khẩu không chính xác");
        setIsLoading(false);
        return;
      }

      // Save session
      saveAccountSession(account);

      // Success
      setUsername("");
      setPassword("");
      onLoginSuccess(account);
    } catch (err) {
      setError("Có lỗi xảy ra. Vui lòng thử lại!");
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Card sx={{ width: "100%", maxWidth: 420, boxShadow: 3 }}>
          <Paper
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              padding: 3,
              textAlign: "center",
              color: "white",
            }}
          >
            <LockIcon sx={{ fontSize: 48, marginBottom: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Misa Kho
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 1, opacity: 0.9 }}>
              Hệ thống tùy chỉnh phiếu chứng từ kho
            </Typography>
          </Paper>

          <CardContent sx={{ padding: 3 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Tên đăng nhập"
                variant="outlined"
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                autoFocus
                size="small"
              />

              <TextField
                fullWidth
                label="Mật khẩu"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mt: 3,
                  mb: 2,
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  fontWeight: "bold",
                }}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Đăng nhập"
                )}
              </Button>

              {/* Fixed height error message area */}
              <Box sx={{ minHeight: "48px", mb: 1 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 0 }}>
                    {error}
                  </Alert>
                )}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  color: "#666",
                  mt: 0,
                  borderTop: "1px solid #eee",
                  pt: 2,
                }}
              >
                Liên hệ bộ phận IT để được hỗ trợ đăng nhập.
                <br />
                Kênh liên hệ: Base - duongnp - 0814614616
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default LoginForm;
